import {
  isCurrentUserInGroup,
  normalizeGroupMetadataValue,
} from "./GroupsSharedUi";

export function findGroupById(groups, groupId) {
  if (!groupId) return null;
  return groups.find((group) => group.id === groupId) || null;
}

export function findUserById(allUsers, userId) {
  if (!userId) return null;
  return allUsers.find((candidate) => candidate.id === userId) || null;
}

export function buildFriendUsers(allUsers, user) {
  return allUsers.filter((candidate) => user?.friends?.includes(candidate.id));
}

export function buildPeopleListState({
  groups,
  peopleListGroupId,
  peopleListType,
  t,
}) {
  const peopleListGroup = findGroupById(groups, peopleListGroupId);
  const peopleListIds = peopleListGroup
    ? peopleListType === "admins"
      ? peopleListGroup.admins
      : peopleListGroup.members
    : [];
  const peopleListTitle =
    peopleListType === "admins"
      ? t("groups_admins_label")
      : t("groups_member_list");
  const peopleListCountLabel =
    peopleListType === "admins"
      ? peopleListIds.length === 1
        ? t("groups_admin_count")
        : t("groups_admins")
      : peopleListIds.length === 1
        ? t("groups_member_count")
        : t("groups_members");

  return {
    peopleListGroup,
    peopleListIds,
    peopleListTitle,
    peopleListCountLabel,
  };
}

export function buildConfirmState({
  confirmAction,
  groups,
  allUsers,
  t,
}) {
  const confirmGroup = confirmAction
    ? findGroupById(groups, confirmAction.groupId)
    : null;
  const confirmTargetUser = confirmAction?.targetUserId
    ? findUserById(allUsers, confirmAction.targetUserId)
    : null;
  const confirmTargetName =
    confirmTargetUser?.username ||
    t("groups_unknown_member", { defaultValue: "Unknown member" });

  const confirmTitle =
    confirmAction?.type === "delete"
      ? t("groups_delete_confirm_title")
      : confirmAction?.type === "leave"
        ? t("groups_leave_confirm_title")
        : confirmAction?.type === "removeMember"
          ? t("groups_remove_member_confirm_title", {
              defaultValue: "Remove member?",
            })
          : confirmAction?.type === "demoteAdmin"
            ? t("groups_remove_admin_confirm_title", {
                defaultValue: "Remove admin?",
              })
            : "";

  const confirmMessage =
    confirmAction?.type === "delete"
      ? t("groups_delete_confirm_text")
      : confirmAction?.type === "leave"
        ? t("groups_leave_confirm_text")
        : confirmAction?.type === "removeMember"
          ? t("groups_remove_member_confirm_text", {
              defaultValue: "Remove {{name}} from this group?",
              name: confirmTargetName,
            })
          : confirmAction?.type === "demoteAdmin"
            ? t("groups_remove_admin_confirm_text", {
                defaultValue: "Remove admin access for {{name}}?",
                name: confirmTargetName,
              })
            : "";

  return {
    confirmGroup,
    confirmTargetUser,
    confirmTargetName,
    confirmTitle,
    confirmMessage,
  };
}

export function buildFilteredGroups({
  groups,
  groupFilterScope,
  groupFilterWaterType,
  groupFilterWayOfFishing,
  groupFilterTypeOfFishing,
  userId,
}) {
  const nextGroups = groups.filter((group) => {
    const normalizedWaterType = normalizeGroupMetadataValue(group.waterType) || "all";
    const normalizedWayOfFishing =
      normalizeGroupMetadataValue(group.wayOfFishing) || "all";
    const normalizedTypeOfFishing =
      normalizeGroupMetadataValue(group.typeOfFishing) || "all";
    const viewerIsInGroup = isCurrentUserInGroup(group, userId);

    if (
      groupFilterWaterType !== "all" &&
      normalizedWaterType !== groupFilterWaterType
    ) {
      return false;
    }

    if (
      groupFilterWayOfFishing !== "all" &&
      normalizedWayOfFishing !== groupFilterWayOfFishing
    ) {
      return false;
    }

    if (
      groupFilterTypeOfFishing !== "all" &&
      normalizedTypeOfFishing !== groupFilterTypeOfFishing
    ) {
      return false;
    }

    if (groupFilterScope === "mine" && !viewerIsInGroup) {
      return false;
    }

    if (groupFilterScope === "suggested" && viewerIsInGroup) {
      return false;
    }

    return true;
  });

  if (groupFilterScope !== "all" || !userId) {
    return nextGroups;
  }

  return [...nextGroups].sort((leftGroup, rightGroup) => {
    const leftIsCurrentUserGroup = isCurrentUserInGroup(leftGroup, userId);
    const rightIsCurrentUserGroup = isCurrentUserInGroup(rightGroup, userId);

    if (leftIsCurrentUserGroup !== rightIsCurrentUserGroup) {
      return Number(rightIsCurrentUserGroup) - Number(leftIsCurrentUserGroup);
    }

    return (Number(rightGroup.createdAt) || 0) - (Number(leftGroup.createdAt) || 0);
  });
}

export function getEligibleFriendUsers(friendUsers, group) {
  return friendUsers.filter(
    (friend) =>
      !group.members.includes(friend.id) &&
      !group.invitedUsers?.includes(friend.id) &&
      !group.pendingJoinRequests?.includes(friend.id)
  );
}

export function getPendingFriendUsers(friendUsers, group) {
  return friendUsers.filter((friend) => group.invitedUsers?.includes(friend.id));
}
